import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import AccessDenied from './AccessDenied';
import LoadingAnimation from './LoadingAnimation';
import { useAuth } from '../contexts/AuthContext';
import projectsService from '../services/projectsService';
import { hasCapability, CAP } from '../utils/permissions';
import { isUserOnProjectTeam } from '../utils/projectTeam';
import type { Project } from '../types';

type GateState =
  | { status: 'loading' }
  | { status: 'allowed' }
  | { status: 'denied'; title: string; message: string };

interface ProjectAccessGateProps {
  children: React.ReactNode;
}

const ProjectAccessGate: React.FC<ProjectAccessGateProps> = ({ children }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const [state, setState] = useState<GateState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    const id = Number(projectId);

    if (!projectId || Number.isNaN(id) || id <= 0) {
      setState({
        status: 'denied',
        title: 'Project not found',
        message: 'This project does not exist or the link is invalid.',
      });
      return;
    }

    setState({ status: 'loading' });

    (async () => {
      try {
        if (user && hasCapability(user, CAP.VIEW_ALL_PROJECTS)) {
          // Still verify the project exists.
          await projectsService.getProjectDetails(id);
          if (!cancelled) setState({ status: 'allowed' });
          return;
        }

        const { project } = await projectsService.getProjectDetails(id);
        if (cancelled) return;

        if (isUserOnProjectTeam(project as Project, user)) {
          setState({ status: 'allowed' });
          return;
        }

        setState({
          status: 'denied',
          title: 'Access denied',
          message: 'You do not have access to this project. Ask a project manager to add you to the team.',
        });
      } catch (err: any) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) {
          setState({
            status: 'denied',
            title: 'Project not found',
            message: 'This project does not exist or the link is invalid.',
          });
          return;
        }
        if (status === 403) {
          setState({
            status: 'denied',
            title: 'Access denied',
            message: 'You do not have access to this project. Ask a project manager to add you to the team.',
          });
          return;
        }
        setState({
          status: 'denied',
          title: 'Access denied',
          message: 'You do not have access to this project. Ask a project manager to add you to the team.',
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, user]);

  if (state.status === 'loading') {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ backgroundColor: 'background.default' }}
      >
        <LoadingAnimation />
      </Box>
    );
  }

  if (state.status === 'denied') {
    return (
      <AccessDenied
        title={state.title}
        message={state.message}
        secondaryAction={{ label: 'My projects', to: '/projects' }}
      />
    );
  }

  return <>{children}</>;
};

export default ProjectAccessGate;
